import { atom } from 'jotai';

import type { ConversationListItem } from 'components/agent-chat/types';
import { atomFamily, atomWithStorage } from 'jotai/utils';

// Which conversation is active, keyed by agent *and* record (see
// `agentScopeKey`) so a thread does not follow the user to another document.
// Persisted to localStorage so reopening the panel resumes the prior thread.
export const activeConversationIdAtomFamily = atomFamily((scopeKey: string) =>
  atomWithStorage<number | null>(`agent-chat:active:${scopeKey}`, null)
);

// Dock geometry for the side panel the chat renders into. Shared across pages
// so every agent dock resizes within the same bounds.
export const AGENT_PANEL_DEFAULT_WIDTH_PX = 480;
export const AGENT_PANEL_MIN_WIDTH_PX = 320;
export const AGENT_PANEL_MAX_WIDTH_PX = 1200;

// Per-agent: whether the dock is open. In-memory only — opening the panel is a
// deliberate act, so it should not persist across reloads.
export const agentPanelOpenAtomFamily = atomFamily((agentType: string) => {
  const a = atom(false);
  a.debugLabel = `agentPanelOpen:${agentType}`;
  return a;
});

// Per-agent: dock width, persisted so a user's preferred width sticks.
export const agentPanelWidthAtomFamily = atomFamily((agentType: string) =>
  atomWithStorage<number>(
    `agent-chat:width:${agentType}`,
    AGENT_PANEL_DEFAULT_WIDTH_PX
  )
);

// Conversation list per agent + record. Carbon can tear down and rebuild the
// history panel's portal at any time, so the rows live outside the component or
// a remount mid-fetch leaves the panel blank.
export const agentConversationsAtomFamily = atomFamily((scopeKey: string) => {
  const a = atom<ConversationListItem[]>([]);
  a.debugLabel = `agentConversations:${scopeKey}`;
  return a;
});

// Which conversation row is in rename mode. Held outside the component for the
// same reason as the list: Carbon rebuilds the history panel's portal freely.
export const agentRenamingConversationAtomFamily = atomFamily(
  (scopeKey: string) => {
    const a = atom<number | null>(null);
    a.debugLabel = `agentRenaming:${scopeKey}`;
    return a;
  }
);
